import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Card from '../../components/ui/Card';
import { SocialAuthButtons } from '../../components/ui/SocialAuthButton';
import { useToast } from '../../contexts/ToastContext';

const Login = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const sessionExpired = searchParams.get('session_expired');
  const sessionMessage = sessionExpired ? 'Session expired due to inactivity' : null;

  const [formData, setFormData] = useState({
    login: '',
    password: '',
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [currentTitleIndex, setCurrentTitleIndex] = useState(0);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { addToCart } = useCart();

  React.useEffect(() => {
    let sessionPlan = null;
    const rawSessionPlan = sessionStorage.getItem('selectedPlan');
    if (rawSessionPlan) {
      try {
        sessionPlan = JSON.parse(rawSessionPlan);
      } catch (e) {
        sessionStorage.removeItem('selectedPlan');
      }
    }

    if (sessionPlan?.id) {
      addToCart(sessionPlan);
    }
  }, [addToCart]);

  // Title rotation effect - cycles through titles every 5 seconds
  const titles = [
    { main: 'Secure Access', sub: 'Your chamas are protected' },
    { main: 'Community First', sub: 'Building wealth together' },
    { main: 'Protected Savings', sub: 'Your money, secured' },
    { main: 'Financial Growth', sub: 'Track and manage contributions' },
    { main: 'Fast & Reliable', sub: 'Seamless transactions' },
  ];

  React.useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTitleIndex((prev) => (prev + 1) % titles.length);
    }, 5000); // Change title every 5 seconds

    return () => clearInterval(interval);
  }, [titles.length]);

  const handleSocialAuth = async (provider) => {
    try {
      // OAuth flow:
      // 1. Redirect to backend OAuth endpoint
      // 2. Backend redirects to provider (Google/Facebook/Apple)
      // 3. Provider redirects back to `/auth/${provider}/callback`
      // 4. Backend exchanges code for tokens, creates/finds user
      // 5. Callback page handles token storage and redirects to dashboard

      const currentUrl = window.location.origin;
      const redirectUri = `${currentUrl}/oauth/callback`;

      // Store intended destination for post-OAuth redirect
      sessionStorage.setItem('oauth_redirect', '/dashboard');

      // Redirect to backend OAuth endpoint
      window.location.href = `/api/v1/auth/social/${provider}?redirect_uri=${encodeURIComponent(redirectUri)}`;
    } catch (error) {
      showToast(`Failed to sign in with ${provider}`, 'error');
      console.error(`${provider} auth error:`, error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    const result = await login(formData);
    setLoading(false);

    if (result.success) {
      const returnTo =
        location.state?.returnTo || sessionStorage.getItem('returnToAfterAuth') || '/dashboard';

      sessionStorage.removeItem('returnToAfterAuth');
      navigate(returnTo, { replace: true });
    } else {
      setErrors({ general: result.message });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0f1115] py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-lg w-full animate-fadeIn">
        {/* Premium Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            {/* Logo Icon */}
            <div className="relative flex-shrink-0">
              {/* Glow Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 via-cyan-500 to-amber-500 rounded-2xl blur-xl opacity-20 animate-pulse" />

              {/* Main Logo */}
              <div className="relative bg-gradient-to-br from-[#1a1f2e] to-[#0f1115] rounded-2xl p-3 border border-white/10 backdrop-blur-sm">
                <svg
                  className="w-14 h-14"
                  viewBox="0 0 120 120"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  {/* Circular Background Accent */}
                  <circle cx="60" cy="60" r="55" fill="url(#logoGradient)" opacity="0.1" />

                  {/* Interlocking Circles - Representing Unity & Collaboration */}
                  <circle
                    cx="45"
                    cy="55"
                    r="18"
                    stroke="url(#logoGradient)"
                    strokeWidth="3.5"
                    fill="none"
                    opacity="0.8"
                  />
                  <circle
                    cx="75"
                    cy="55"
                    r="18"
                    stroke="url(#logoGradient)"
                    strokeWidth="3.5"
                    fill="none"
                    opacity="0.8"
                  />

                  {/* Center Diamond - Representing Value & Trust */}
                  <path d="M60 40 L70 55 L60 70 L50 55 Z" fill="url(#logoGradient)" opacity="0.9" />

                  {/* Coins Stack - Financial Symbol */}
                  <ellipse cx="60" cy="75" rx="15" ry="4" fill="url(#logoGradient)" opacity="0.6" />
                  <ellipse cx="60" cy="72" rx="15" ry="4" fill="url(#logoGradient)" opacity="0.7" />
                  <ellipse cx="60" cy="69" rx="15" ry="4" fill="url(#logoGradient)" opacity="0.8" />

                  {/* Connecting Lines - Community Network */}
                  <path
                    d="M45 73 Q60 80 75 73"
                    stroke="url(#logoGradient)"
                    strokeWidth="2"
                    opacity="0.5"
                    strokeLinecap="round"
                  />

                  {/* Gradient Definition */}
                  <defs>
                    <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#10b981" />
                      <stop offset="50%" stopColor="#06b6d4" />
                      <stop offset="100%" stopColor="#fbbf24" />
                    </linearGradient>
                  </defs>
                </svg>
              </div>
            </div>

            {/* Brand Name */}
            <div className="text-left">
              <h1 className="text-4xl font-bold tracking-tight">
                <span className="bg-gradient-to-r from-emerald-400 via-cyan-400 to-amber-300 bg-clip-text text-transparent">
                  Group
                </span>
                <span className="text-white ml-2">Hela</span>
              </h1>
              <p className="text-emerald-400/60 text-xs font-medium tracking-widest uppercase mt-1">
                Financial Unity Platform
              </p>
            </div>
          </div>

          {/* Scrolling Tagline */}
          <div className="transition-opacity duration-500" key={currentTitleIndex}>
            <h5 className="text-lg font-semibold text-white">
              {titles[currentTitleIndex].main}{' '}
              <span className="text-[#d8d2c5] font-normal text-sm ml-2">
                — {titles[currentTitleIndex].sub}
              </span>
            </h5>
          </div>
        </div>

        <Card className="shadow-2xl bg-[#131820]/95 border border-white/10 backdrop-blur">
          {/* Session Expired Message */}
          {sessionMessage && (
            <div className="mb-6 bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
              <div className="flex gap-3">
                <svg
                  className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-amber-200">{sessionMessage}</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {errors.general && (
            <div className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4">
              <div className="flex gap-3">
                <svg
                  className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-red-200">{errors.general}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <Input
              label="Email or Phone Number"
              name="login"
              type="text"
              value={formData.login}
              onChange={handleChange}
              error={errors.login}
              placeholder="Enter your email or phone"
              leftIcon={
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  />
                </svg>
              }
              required
            />

            <Input
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              error={errors.password}
              placeholder="Enter your password"
              leftIcon={
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  />
                </svg>
              }
              required
            />

            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  className="w-4 h-4 rounded border-white/20 bg-white/5 text-emerald-500 focus:ring-emerald-500/50"
                />
                <span className="ml-2 text-sm text-[#d8d2c5]">Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-sm font-medium text-emerald-400 hover:text-emerald-300"
              >
                Forgot password?
              </Link>
            </div>

            <Button type="submit" fullWidth loading={loading} size="lg">
              Sign In
            </Button>
          </form>

          {/* Social Authentication - Below Sign In */}
          <div className="mt-4">
            <SocialAuthButtons
              providers={['google', 'facebook']}
              onProviderClick={handleSocialAuth}
              disabled={loading}
              mode="signin"
              showDivider={false}
              layout="horizontal"
            />
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#131820] text-[#d8d2c5]">
                  Don&apos;t have an account?
                </span>
              </div>
            </div>

            <Link to="/register" className="mt-4 block">
              <Button variant="outline" fullWidth size="lg">
                Create Account
              </Button>
            </Link>
          </div>
        </Card>

        {/* Footer */}
        <p className="mt-8 text-center text-sm text-[#d8d2c5]">
          By signing in, you agree to our{' '}
          <a href="#" className="text-emerald-400 hover:text-emerald-300">
            Terms
          </a>{' '}
          and{' '}
          <a href="#" className="text-emerald-400 hover:text-emerald-300">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
};

export default Login;
